import { DatePipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, ElementRef, EventEmitter, input, Input, OnInit, Output, Signal, signal, ViewChild, WritableSignal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { AiUtility, CalculationsUtil, FORMULA_CONSTANTS, NUMBER_STATUS } from 'countable@helpers';
import { AmorCalculationModel, AmortizationDetailModel, AmortizationInputsModel, AmortizationScheduleModel, CategoryModel, GlGroupModel, GLSelectionModel, GlSubGroupModel, GlTbModel, GLTbYearModel, INPUT_PROMPT_TYPES, LoanInfoModel, LsAccountsModel, LucaFileModel, LucaInputsModel, METHODS, MethodsModel, PROMPT_MSG_TYPES, SavedChatHistoryModel, ScheduleAdjustmentModel, TbAccountModel } from 'countable@model';
import { KatexOptions } from 'ngx-markdown';
import { PlaidInstitution } from '../../../model/plaid.model';

@Component({
  selector: 'ai-table-input',
  templateUrl: './ai-table-inputs.component.html',
  styleUrl: './ai-table-inputs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AiTableInputsComponent implements OnInit, AfterViewInit {

  @Input() set inputInfo(info: Partial<LucaInputsModel>) {
    console.log('Input Information', info);
    this.inputType.set(info?.inputType);
    this.setInputView(info);
  }

  @Input() set allowSelectionChange(history: SavedChatHistoryModel) {
    if (history?.promptId === 3 && history?.messages?.length > 0) {
      const isGlDisabled = this.checkGlEligibility(JSON.parse(JSON.stringify(history)));
      this.isReadOnly.set(isGlDisabled);
    }
    if (history?.promptId === 8 && history?.messages?.length > 0) {
      const isCAASDisabled = this.checkCAASEligibility(JSON.parse(JSON.stringify(history)));
      this.isReadOnly.set(isCAASDisabled);
    }
  }

  @Output() updateSelection: EventEmitter<Partial<LucaInputsModel>> = new EventEmitter<Partial<LucaInputsModel>>(true);
  @Output() draftSelection: EventEmitter<Partial<LucaInputsModel>> = new EventEmitter<Partial<LucaInputsModel>>(true);
  @ViewChild('fileUploader') fileUploader: ElementRef;

  public inputFiles = input<File[]>([]);
  public readonly aiUtil: typeof AiUtility = AiUtility;
  public readonly calUtil: typeof CalculationsUtil = CalculationsUtil;
  public hasValidVr: WritableSignal<boolean> = signal<boolean>(false);
  public vrPercent: FormControl<string> = new FormControl<string>('10.00');
  public vrValue: FormControl<string> = new FormControl<string>('0.00');
  public selectedFile: WritableSignal<File[] | null> = signal<File[] | null>(null);
  public glData: WritableSignal<GLTbYearModel[]> = signal<GLTbYearModel[]>([]);
  public glRows: WritableSignal<GlTbModel[]> = signal<GlTbModel[]>(null);
  public originalTbRows: WritableSignal<GlTbModel[]> = signal<GlTbModel[]>(null);
  public selectedTbRows: WritableSignal<GlTbModel[]> = signal<GlTbModel[]>([]);
  public selectedYear: WritableSignal<string> = signal<string>('');
  public isAllSelected: WritableSignal<boolean> = signal<boolean>(false);
  public inputType: WritableSignal<PROMPT_MSG_TYPES> = signal<PROMPT_MSG_TYPES>(null);
  public lsAccountsInfo: WritableSignal<LsAccountsModel[]> = signal<LsAccountsModel[]>([]);
  public tbAccountsInfo: WritableSignal<LsAccountsModel[]> = signal<LsAccountsModel[]>([]);
  public disableSubmit: WritableSignal<boolean> = signal<boolean>(false);
  public amortizationInputs: WritableSignal<AmortizationInputsModel[]> = signal<AmortizationInputsModel[]>([]);
  public methods: WritableSignal<MethodsModel[]> = signal<MethodsModel[]>(structuredClone(METHODS));
  public categoryList: WritableSignal<CategoryModel[]> = signal<CategoryModel[]>([]);
  public isReadOnly: WritableSignal<boolean> = signal<boolean>(false);
  public amortizationSchedule: WritableSignal<AmortizationScheduleModel[]> = signal<AmortizationScheduleModel[]>([]);
  public adjustmentEntries: WritableSignal<ScheduleAdjustmentModel[]> = signal<ScheduleAdjustmentModel[]>([]);
  public caasGl: WritableSignal<any> = signal<any>(null);
  public caasTb: WritableSignal<any> = signal<any>(null);
  public hasConfirmation: WritableSignal<boolean> = signal<boolean>(false);
  public hasLimitExceeded: WritableSignal<boolean> = signal<boolean>(false);
  public hasFilesUploaded: Signal<boolean> = computed(() => {
    return (this.selectedFile()?.length > 0);
  });
  public attachedFiles: WritableSignal<LucaFileModel[]> = signal<LucaFileModel[]>(null);
  public showInputs: Signal<INPUT_PROMPT_TYPES> = computed(() => {
    return this.aiUtil?.transformInputTypes(this.inputType());
  })
  public messages: WritableSignal<string> = signal<string>('');
  public hasProceedAction: WritableSignal<boolean> = signal<boolean>(false);
  public hasValidInputs: Signal<boolean> = computed(() => {
    return this.amortizationInputs()?.every(i => i?.categoryId > 0);
  });
  public showFIleInput: Signal<boolean> = computed(() => {
    return !this.isReadOnly() && !this.hasProceedAction();
  });
  public loanInputs: WritableSignal<LoanInfoModel> = signal<LoanInfoModel>(null);
  public groups: WritableSignal<GlGroupModel[]> = signal<GlGroupModel[]>([]);
  public subGroups: WritableSignal<GlSubGroupModel[]> = signal<GlSubGroupModel[]>([]);
  public selectedGroups: GlGroupModel[] = [];
  public selectedSubGroups: GlSubGroupModel[] = [];
  public selectedLs: LsAccountsModel[] = [];
  public selectedAccounts: LsAccountsModel[] = [];
  public inValidEntries: Signal<string[]> = computed(() => {
    return this.adjustmentEntries()?.filter(r => r?.rowType !== 'TOTAL' && !(r?.trialBalId > 0))
      ?.map(r => r?.description);
  });

  public hasMissingInputs: WritableSignal<boolean> = signal<boolean>(false);
  public frequencyList: WritableSignal<{value: string,label: string}[]> = signal<{value: string,label: string}[]>([
    { value: 'weekly', label: 'Weekly' },
    { value: 'biweekly', label: 'Biweekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' }
  ]);
  public frequencyCtrl: FormControl<string> = new FormControl<string>('', Validators.required);
  public interestCtrl: FormControl<string> = new FormControl<string>('', Validators.required);
  public loanStartDate: WritableSignal<string> = signal<string>('');
  public transformedLoanDate: Signal<string> = computed(() => {
    return this.convertToIsoUtc(this.loanStartDate())
  })
  public principalCtrl: FormControl<string> = new FormControl<string>('', Validators.required);
  public loanTenure: FormControl<string> = new FormControl<string>('', Validators.required);
  public capitalFormulae: typeof FORMULA_CONSTANTS = FORMULA_CONSTANTS;
  public showMethodFormulae: WritableSignal<string> = signal<string>(null);
  public formulaKeys: string[] = [
    'depreciationFormula',
    'declineBalanceFormula',
    'doubleDecliningFormula',
    'sumOfYearsFormula',
    'unitsOfProductionFormula'
  ];
  public katexInfo: WritableSignal<KatexOptions> = signal<KatexOptions>({
    displayMode: false,
    delimiters: [
      {left: '$$', right: '$$', display: true},
      {left: '$', right: '$', display: false},
      {left: '\(', right: '\)', display: false},
      {left: '\[', right: '\]', display: true},
      {left: "\begin{equation}", right: "\end{equation}", display: true},
      {left: "\begin{align}", right: "\end{align}", display: true},
    ],
    throwOnError : false
  });
  public tbAccounts: WritableSignal<TbAccountModel[]> = signal<TbAccountModel[]>([]);
  public tbAccCtrl: FormControl<string> = new FormControl<string>(null);
  public plaidAccCtrl: FormControl<any> = new FormControl<any>(null);
  public plaidInstitutions: WritableSignal<PlaidInstitution[]> = signal<PlaidInstitution[]>([]);
  public disableSubmitRecon: Signal<boolean> = computed(() => {
    return false;
  });
  public hasNoValidRecon: WritableSignal<boolean> = signal<boolean>(false);


  constructor(private cdr: ChangeDetectorRef, private datePipe: DatePipe) {
  }

  ngOnInit(): void {
  }

  ngAfterViewInit() {
    if (this.inputType() === 'VARIANCE_ANALYSIS_INPUTS') {
      this.checkVrStatus();
    }
  }

  private setInputView(info: Partial<LucaInputsModel>) {
    if (info?.inputType === 'GENERAL_LEDGER') {
      this.setUpGL(structuredClone(info));
    }
    if (info?.inputType === 'CAAS_PREFERENCES' || info?.inputType === 'CAPITAL_ASSETS') {
      this.amortizationInputs.set(info?.prefAccounts);
      this.categoryList.set(info?.prefCategories);
      this.isReadOnly.set(info?.isDisabled);
    }
    if (info?.inputType === 'CAAS_SCHEDULE') {
      this.amortizationSchedule.set(info?.scheduleRows);
      this.adjustmentEntries.set(info?.adjustmentEntries);
      this.selectedYear.set(info?.year);
      this.amortizationInputs.set(info?.prefAccounts);
      this.caasGl.set(info?.generalLedger);
      this.caasTb.set(info?.tbAccounts);
    }
    if (info?.inputType === 'IS' || info?.inputType === 'HST') {
      this.hasConfirmation.set(!!info?.hasConfirmation);
      this.messages.set(info?.message);
      this.hasProceedAction.set(!!info?.hasProceedAction);
      if (info?.isDisabled) {
        this.isReadOnly.set(true);
        this.attachedFiles.set(info?.attachedFiles);
      } else {
        this.selectedFile.set(this.inputFiles())
      }
    }
    if(info?.inputType === 'RECONCILIATION') {
      this.tbAccounts.set(info?.reconInfo?.tbAccounts);
      this.plaidInstitutions.set(info?.reconInfo?.plaidInstitutions);
      this.messages.set(info?.message);
      this.hasNoValidRecon.set(info?.reconInfo?.hasNoValidRecon);
      if (info?.isDisabled) {
        this.isReadOnly.set(true);
        this.tbAccCtrl.setValue(info?.reconInfo?.selectedTBAccountUUID);
        this.tbAccCtrl.disable();
        this.plaidAccCtrl.setValue(info?.reconInfo?.selectedPlaidAccount);
        this.plaidAccCtrl.disable();
        this.attachedFiles.set(info?.attachedFiles);
      }
    }
    if (info?.inputType === 'LAS') {
      this.hasConfirmation.set(!!info?.hasInValidFile);
      this.messages.set(info?.message);
      this.hasProceedAction.set(!!info?.hasProceedAction);
      this.hasMissingInputs.set(info?.hasMissingInputs);
      this.loanInputs.set(info?.loanInfo);
      this.frequencyCtrl.setValue(info?.loanInfo?.frequency);
      this.loanStartDate.set(info?.loanInfo?.loanStartDate);
      this.loanTenure.setValue(info?.loanInfo?.loanTenure?.toString());
      this.principalCtrl.setValue(info?.loanInfo?.principalAmount?.toString());
      this.interestCtrl.setValue(info?.loanInfo?.interestRate?.toString());

      if (info?.isDisabled) {
        this.isReadOnly.set(true);
        this.attachedFiles.set(info?.attachedFiles);
      }
    }
    if (info?.inputType === 'VARIANCE_ANALYSIS_INPUTS') {
      this.isReadOnly.set(info?.isDisabled);
      this.vrPercent.setValue(info?.matPercent?.toString() ?? '10.00');
      this.vrValue.setValue(info?.matVal?.toString() ?? '0.00');
      if (this.isReadOnly()) {
        this.vrValue.disable();
        this.vrPercent.disable();
      }
    }
  }

  private checkGlEligibility(chatHistory: SavedChatHistoryModel): boolean {
    const messages = chatHistory?.messages;
    if (messages && messages.length > 3) {
      const messageAtIndex2 = messages[2];
      if (messageAtIndex2?.messageType === 'WORKFLOW' && messageAtIndex2?.specialPromptMessage === 'GENERAL_LEDGER') {
        return true;
      }
    }
    return false;
  }

  private checkCAASEligibility(chatHistory: SavedChatHistoryModel) {
    const messages = chatHistory?.messages;
    if (messages && messages.length > 4) {
      const msgAt3 = messages[3];
      if (msgAt3?.messageType === 'WORKFLOW' && msgAt3?.specialPromptMessage === 'CAAS_SCHEDULE') {
        return true;
      }
    }
    return false;
  }

  changeTbYear(event: any) {
    if (event?.value) {
      const Obj = this.glData()?.find(g => g?.year === event?.value);
      this.changeYear(Obj);
    }
  }

  private filterGLSelection(): GlTbModel[] {
    const originalRows = structuredClone(this.originalTbRows());
    const accounts = this.selectedAccounts?.map(r => r?.trialBalId);
    if (accounts && accounts?.length > 0) {
      return originalRows?.filter(r => accounts?.includes(r?.trailbalanceid));
    }
    const lsRows = this.selectedLs?.map(r => r?.lsId);
    if (lsRows && lsRows?.length > 0) {
      return originalRows?.filter(r => lsRows?.includes(r?.leadsheetid));
    }
    const subGroups = this.selectedSubGroups?.map(r => r?.subGroupId);
    if (subGroups && subGroups?.length > 0) {
      return originalRows?.filter(r => subGroups?.includes(r?.finsubgroupid));
    }
    const groups = this.selectedGroups?.map(r => r?.groupId);
    if (groups && groups?.length > 0) {
      return originalRows?.filter(r => groups?.includes(r?.fingroupid));
    }
  }

  public submitSelection() {
    let filteredRows: GlTbModel[] = this.filterGLSelection();
    let formattedRows: GlTbModel[] = [];
    if (filteredRows && filteredRows?.length > 0) {
      formattedRows = this.calculateAndFormatRows(structuredClone(filteredRows));
    }
    this.selectedTbRows.set(formattedRows);
    const Obj: LucaInputsModel = new LucaInputsModel<GlTbModel[]>();
    Obj.inputType = 'GENERAL_LEDGER';
    Obj.isDisabled = false;
    const info: GLSelectionModel = new GLSelectionModel();
    info.originalGl = structuredClone(this.glData());
    info.year = this.selectedYear();
    info.groups = this.selectedGroups;
    info.subGroups = this.selectedSubGroups;
    info.leadSheets = this.selectedLs;
    info.accounts = this.selectedAccounts;
    info.selectedRows = structuredClone(this.selectedTbRows());
    info.isDisabled = false;
    Obj.glSelection = structuredClone(info);
    this.updateSelection.emit(Obj);
    this.draftSelection.emit(null);
    this.disableSubmit.set(true);
  }

  public clearSelection(isResetRows: boolean = false) {
    this.selectedGroups = [];
    this.selectedSubGroups = [];
    this.selectedLs = [];
    this.selectedAccounts = [];
    isResetRows && this.selectedTbRows.set([]);
    this.setAllDropdowns();
  }

  public changeYear(group: GLTbYearModel, isClearAll: boolean = true) {
    this.glRows.set((group?.tbRowObj));
    this.originalTbRows.set(structuredClone(group?.tbRowObj));
    this.selectedYear.set(group?.year);
    isClearAll && this.clearSelection(false);
    !isClearAll && this.setAllDropdowns();
  }

  public onGroupChange(event: GlGroupModel[]) {
    if (event?.length === 0) {
      this.clearSelection(false);
      return;
    }
    this.updateSubGroups(structuredClone(this.selectedGroups));
  }

  public onSubGroupChange(event: GlSubGroupModel[]) {
    if (event?.length === 0) {
      this.setLsTbAccountsList();
      return;
    }
    this.updateLeadSheet(structuredClone(event));
  }

  public onLsChange(event: LsAccountsModel[]) {
    if (event?.length === 0) {
      this.setTbAccountsList();
      return;
    }
    this.updateAccounts(structuredClone(event));
  }

  public onAccountsChange(event: LsAccountsModel[]) {
  }

  private updateSubGroups(selectedGroups: GlGroupModel[]) {
    const filteredSubGroups = this.aiUtil?.filterSortSubGroups(structuredClone(
      this.aiUtil?.getGroupsFromGl(JSON.parse(JSON.stringify(this.originalTbRows())),
        selectedGroups?.map(g => g?.groupId))));
    this.subGroups.set(filteredSubGroups);
    this.updateLeadSheet(structuredClone(filteredSubGroups));
  }

  private updateLeadSheet(filteredSubGroups: GlSubGroupModel[]) {
    const filteredLs = this.aiUtil?.filterSortLs(JSON.parse(JSON.stringify(
      this.aiUtil?.getSubGroupsFromGl(JSON.parse(JSON.stringify(this.originalTbRows())), filteredSubGroups))));
    this.lsAccountsInfo.set(structuredClone(filteredLs));
    this.updateAccounts(structuredClone(filteredLs));
  }

  private updateAccounts(filteredLs: LsAccountsModel[]) {
    this.tbAccountsInfo.set(this.aiUtil?.filterSortAccounts(
      JSON.parse(JSON.stringify(
        this.aiUtil?.getLsFromGl(JSON.parse(JSON.stringify(this.originalTbRows())), filteredLs)
      ))));
  }

  private setAllDropdowns() {
    this.groups.set(this.aiUtil?.filterSortGroups(JSON.parse(JSON.stringify(this.originalTbRows()))));
    this.subGroups.set(this.aiUtil?.filterSortSubGroups(JSON.parse(JSON.stringify(this.originalTbRows()))));
    this.lsAccountsInfo.set(this.aiUtil?.filterSortLs(JSON.parse(JSON.stringify(this.originalTbRows()))));
    this.tbAccountsInfo.set(structuredClone(this.aiUtil?.filterSortAccounts(JSON.parse(JSON.stringify(this.originalTbRows())))));
  }

  private setLsTbAccountsList() {
    const originalRows: GlTbModel[] = JSON.parse(JSON.stringify(this.originalTbRows()));
    if (this.selectedGroups?.length > 0) {
      const filterLsByGroups = originalRows?.filter(r =>
        this.selectedGroups?.map(g => g?.groupId)?.includes(r?.fingroupid))
        ?.map(r => r?.leadsheetid);
      const filterLs = originalRows?.filter(r => filterLsByGroups.includes(r?.leadsheetid));
      const filteredLs = this.aiUtil?.filterSortLs(JSON.parse(JSON.stringify(
        filterLs)));
      this.lsAccountsInfo.set(filteredLs);
      this.setTbAccountsList();
      return;
    }
    this.lsAccountsInfo.set(this.aiUtil?.filterSortLs(JSON.parse(JSON.stringify(originalRows))));
    this.tbAccountsInfo.set(structuredClone(this.aiUtil?.filterSortAccounts(JSON.parse(JSON.stringify(originalRows)))));
  }

  private setTbAccountsList() {
    const originalRows: GlTbModel[] = JSON.parse(JSON.stringify(this.originalTbRows()));
    if (this.selectedSubGroups?.length > 0) {
      const subGroupIds = this.selectedSubGroups?.map(r => r?.subGroupId);
      const filterLs = originalRows?.filter(r => subGroupIds.includes(r?.finsubgroupid));
      const filteredLsIds = filterLs?.map(r => r?.leadsheetid);
      const filteredTbAccounts = this.aiUtil?.filterSortAccounts(JSON.parse(JSON.stringify(
        originalRows?.filter(r => filteredLsIds?.includes(r?.leadsheetid)))));
      this.tbAccountsInfo.set(filteredTbAccounts);
      return;
    }
    if (this.selectedGroups?.length > 0) {
      const groups = this.selectedGroups?.map(g => g?.groupId);
      const filterLsByGroups = originalRows?.filter(r => groups.includes(r?.fingroupid))?.map(r => r?.leadsheetid);
      const filterLs = originalRows?.filter(r => filterLsByGroups.includes(r?.leadsheetid));
      const filteredTbAccounts = this.aiUtil?.filterSortAccounts(JSON.parse(JSON.stringify(
        filterLs)));
      this.tbAccountsInfo.set(filteredTbAccounts);
      return;
    }
    this.tbAccountsInfo.set(structuredClone(this.aiUtil?.filterSortAccounts(JSON.parse(JSON.stringify(originalRows)))));
  }

  private calculateAndFormatRows(rows: GlTbModel[]): GlTbModel[] {
    rows?.forEach(r => {
      const openBalStatus: NUMBER_STATUS = this.calUtil.statusOfValue(r?.opening_balance);
      r.opening_balance = this.aiUtil.convertNumValue(r.opening_balance, openBalStatus === 'NEGATIVE');
      const totalBalStatus: NUMBER_STATUS = this.calUtil.statusOfValue(r?.total_balance);
      r.total_balance = this.aiUtil.convertNumValue(r?.total_balance, totalBalStatus === 'NEGATIVE');
      r?.transactions?.forEach(t => {
        const amountStatus: NUMBER_STATUS = this.calUtil.statusOfValue(t?.amount);
        const balStatus: NUMBER_STATUS = this.calUtil.statusOfValue(t?.balance);
        t.amount = this.aiUtil.convertNumValue(t?.amount, amountStatus === 'NEGATIVE');
        t.balance = this.aiUtil.convertNumValue(t?.balance, balStatus === 'NEGATIVE');
      });
    });
    return structuredClone(rows);
  }

  public setDraftSelection() {
    const Obj: LucaInputsModel = new LucaInputsModel<GlTbModel[]>();
    const info: GLSelectionModel = new GLSelectionModel();
    Obj.inputType = 'GENERAL_LEDGER';
    Obj.isDisabled = false;
    info.year = this.selectedYear();
    info.originalGl = structuredClone(this.glData());
    info.selectedRows = structuredClone(this.selectedTbRows());
    info.groups = this.selectedGroups;
    info.subGroups = this.selectedSubGroups;
    info.leadSheets = this.selectedLs;
    info.accounts = this.selectedAccounts;
    info.isDisabled = false;
    Obj.glSelection = structuredClone(info);
    this.draftSelection.emit(Obj);
  }

  public submitAmortizationInputs(type: 'DRAFT' | 'SAVED' = 'SAVED') {
    const Obj: LucaInputsModel = new LucaInputsModel<any>();
    Obj.inputType = 'CAAS_PREFERENCES';

    const inputs = structuredClone(this.amortizationInputs());
    inputs?.forEach(i => {
      i.estimatedProduction = this.aiUtil?.formatNumber(i?.estimatedProduction) ?? 0.00;
      i.residualValue = this.aiUtil?.formatNumber(i?.residualValue) ?? 0.00;
      i.unitProduced = this.aiUtil?.formatNumber(i?.unitProduced) ?? 0.00;
    });
    Obj.prefAccounts = structuredClone(inputs);
    Obj.prefCategories = structuredClone(this.categoryList());
    Obj.isDisabled = type === 'SAVED';
    if (type === 'SAVED') {
      this.isReadOnly.set(true);
      this.updateSelection.emit(Obj);
    }
    if (type === 'DRAFT') {
      this.draftSelection.emit(Obj);
    }
  }

  public changeInputs(event: MatSelectChange, type: 'METHOD' | 'CATEGORY' | 'HALF_YEAR', tbRowId: number) {
    const inputs = structuredClone(this.amortizationInputs());
    const Obj = inputs[tbRowId];
    if (type === 'CATEGORY') {
      const categoryId = event?.value ? +event?.value : 0;
      const category = this.categoryList()?.find(c => c?.categoryId === categoryId);
      Obj.categoryId = category?.categoryId;
      Obj.category = category?.categoryName;
      Obj.methodId = category?.methodId;
      Obj.methodName = category?.methodName ?? category?.method_name;
      Obj.usefulLife = category?.usefulLife;
      Obj.halfYearRule = 'Yes';
      Obj.rate = category.rate;
      inputs[tbRowId] = structuredClone(Obj);
      this.amortizationInputs.set(structuredClone(inputs));
    }
    if (type === 'METHOD') {
      const methodId = event?.value ? +event?.value : 0;
      Obj.methodId = methodId;
      Obj.methodName = METHODS?.find(m => m?.methodId === methodId)?.methodName;
      inputs[tbRowId] = structuredClone(Obj);
      this.amortizationInputs.set(structuredClone(inputs));
    }
    if (type === 'HALF_YEAR') {
      Obj.halfYearRule = event?.value ? event?.value : 'No';
      inputs[tbRowId] = structuredClone(Obj);
      this.amortizationInputs.set(structuredClone(inputs));
    }
    this.submitAmortizationInputs('DRAFT');
  }

  public updateLineItems(event, type: 'RV' | 'UP' | 'EP' | 'RL' | 'RATE' | 'UL', tbRowId: number, row: AmortizationInputsModel) {

    const val = event?.target as HTMLInputElement;
    const value = val?.value ?? '0.00';
    const transformedValue = this.aiUtil?.formatAndTransformValue(value);
    switch (type) {
      case 'RL':
        row.remainingLife = transformedValue;
        break;
      case 'EP':
        row.estimatedProduction = transformedValue;
        break;
      case 'RV':
        row.residualValue = transformedValue;
        break;
      case 'UP':
        row.unitProduced = transformedValue;
        break;
      case 'RATE':
        row.rate = transformedValue;
        break;
      case 'UL':
        row.usefulLife = transformedValue;
        break;
    }
    const inputs = JSON.parse(JSON.stringify(this.amortizationInputs()));
    inputs[tbRowId] = structuredClone(row);
    this.amortizationInputs.set(structuredClone(inputs));
    this.submitAmortizationInputs('DRAFT');
  }

  public transformCAASRow(event: KeyboardEvent, allowMinus: boolean = false) {
    const val = event?.target as HTMLInputElement;
    const key = event?.key;
    const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', 'Backspace', 'Tab', 'Delete', 'ArrowLeft', 'ArrowRight'];
    if (!allowedKeys?.includes(key) || (allowMinus && key === '-')) {
      event?.preventDefault();
    }
    if (key === '.' && val?.value?.includes('.')) {
      event?.preventDefault();
    }
    if (key === '-' && val?.selectionStart !== 0) {
      event.preventDefault();
    }
    this.checkVrStatus();
  }

  public formatTransformInput(event: KeyboardEvent, allowMinus: boolean = false) {
    const val = event?.target as HTMLInputElement;
    const key = event?.key;
    const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', 'Backspace', 'Tab', 'Delete', 'ArrowLeft', 'ArrowRight'];
    if (!allowedKeys?.includes(key) || (allowMinus && key === '-')) {
      event?.preventDefault();
    }
    if (key === '.' && val?.value?.includes('.')) {
      event?.preventDefault();
    }
    if (key === '-' && val?.selectionStart !== 0) {
      event.preventDefault();
    }
  }


  public submitAmortizationSchedule() {
    const Obj: LucaInputsModel = new LucaInputsModel<any>();
    Obj.data = structuredClone(this.amortizationSchedule());
    Obj.scheduleRows = structuredClone(this.amortizationSchedule());
    Obj.adjustmentEntries = structuredClone(this.adjustmentEntries());
    Obj.generalLedger = structuredClone(this.caasGl());
    Obj.tbAccounts = structuredClone(this.caasTb());
    Obj.prefAccounts = structuredClone(this.amortizationInputs());
    Obj.inputType = 'CAAS_SCHEDULE';
    this.updateSelection.emit(Obj);
  }

  public updateCAASRow(event, type: 'ADD' | 'DIS' | 'TB', tbRowId: number) {
    const val = event?.target as HTMLInputElement;
    const value = val?.value ?? '';
    const transformedValue = this.calUtil?.transformValue(value);
    const schedule = structuredClone(this.amortizationSchedule());
    const Obj = structuredClone(schedule[tbRowId]);
    switch (type) {
      case 'ADD':
        Obj.addAdj = transformedValue;
        break;
      case 'DIS':
        Obj.disAdj = transformedValue;
        break;
      case 'TB':
        Obj.balTb = transformedValue;
        break;
    }
    if (type === 'ADD' || type === 'DIS') {
      const selectedInput = this.amortizationInputs()?.find(i => i?.trialBalId === Obj?.trialBalId);
      // When ADD/DIS Changed The Columns Should Be Changed Accordingly -- CB1,A,CB2,CY,UO
      Obj.closingBal = this.aiUtil?.calculateCB1(Obj?.openingBalOne, Obj?.addAdj, Obj?.disAdj);
      // this.updateAmorDetail({...Obj}, JSON.parse(JSON.stringify(selectedInput)));
      Obj.closingBalTb = this.aiUtil?.performCalculation(Obj?.openingBalTb, Obj?.amortization, 'ADD');
      Obj.currYear = this.aiUtil?.performCalculation(Obj?.closingBal?.toString(), Obj?.closingBalTb?.toString());
      Obj.underOver = this.calUtil?.performCalculation(Obj?.amortization?.toString(), Obj?.balTb?.toString());
    }
    if (type === 'TB') {
      // Calculate Only Under/Over
      Obj.underOver = this.aiUtil?.performCalculation(Obj?.amortization?.toString(), Obj?.balTb?.toString());
    }
    schedule[tbRowId] = structuredClone(Obj);
    this.updateScheduleValues(structuredClone(schedule));
  }

  updateScheduleValues(originalRows: AmortizationScheduleModel[]) {
    const tbRows = originalRows?.filter(r => r?.rowType === 'TB');
    const balanceRow = originalRows?.find(r => r?.rowType === 'Balance');
    const calTotalRow: AmortizationScheduleModel = this.calUtil?.getRowInfo('Total');
    const scheduleRows = this.calUtil?.formatScheduleRows(structuredClone(tbRows));

    scheduleRows && scheduleRows?.forEach(r => {
      const openingBalOneStatus = this.calUtil?.statusOfValue(r?.openingBalOne);
      if (openingBalOneStatus !== 'NOT_NUMBER') {
        [calTotalRow.openingBalOne, r.openingBalOne] = this.calUtil?.getUpdatedTotalAndRowValue(calTotalRow?.openingBalOne, r?.openingBalOne,
          openingBalOneStatus === 'NEGATIVE');
      }
      const addAdjStatus = this.calUtil?.statusOfValue(r?.addAdj);
      if (addAdjStatus !== 'NOT_NUMBER') {
        [calTotalRow.addAdj, r.addAdj] = this.calUtil?.getUpdatedTotalAndRowValue(calTotalRow?.addAdj, r?.addAdj,
          addAdjStatus === 'NEGATIVE');
      }
      const disAdj = this.calUtil?.statusOfValue(r?.disAdj);
      if (disAdj !== 'NOT_NUMBER') {
        [calTotalRow.disAdj, r.disAdj] = this.calUtil?.getUpdatedTotalAndRowValue(calTotalRow.disAdj, r?.disAdj,
          disAdj === 'NEGATIVE');
      }
      const cb1Status = this.calUtil?.statusOfValue(r?.closingBal);
      if (cb1Status !== 'NOT_NUMBER') {
        [calTotalRow.closingBal, r.closingBal] = this.calUtil?.getUpdatedTotalAndRowValue(calTotalRow.closingBal, r?.closingBal,
          cb1Status === 'NEGATIVE');
      }
      const openBalTbStatus = this.calUtil?.statusOfValue(r?.openingBalTb);
      if (openBalTbStatus !== 'NOT_NUMBER') {
        [calTotalRow.openingBalTb, r.openingBalTb] = this.calUtil?.getUpdatedTotalAndRowValue(calTotalRow.openingBalTb, r?.openingBalTb,
          openBalTbStatus === 'NEGATIVE');
      }
      const amorStatus = this.calUtil?.statusOfValue(r?.amortization);
      if (amorStatus !== 'NOT_NUMBER') {
        [calTotalRow.amortization, r.amortization] = this.calUtil?.getUpdatedTotalAndRowValue(calTotalRow.amortization, r?.amortization,
          amorStatus === 'NEGATIVE');
      }
      const closingBalTbStatus = this.calUtil?.statusOfValue(r?.closingBalTb);
      if (closingBalTbStatus !== 'NOT_NUMBER') {
        [calTotalRow.closingBalTb, r.closingBalTb] = this.calUtil?.getUpdatedTotalAndRowValue(calTotalRow.closingBalTb, r?.closingBalTb,
          closingBalTbStatus === 'NEGATIVE');
      }
      const cyStatus = this.calUtil?.statusOfValue(r?.currYear);
      if (cyStatus !== 'NOT_NUMBER') {
        [calTotalRow.currYear, r.currYear] = this.calUtil?.getUpdatedTotalAndRowValue(calTotalRow.currYear, r?.currYear,
          cyStatus === 'NEGATIVE');
      }
      const pyStatus = this.calUtil?.statusOfValue(r?.prevYear);
      if (pyStatus !== 'NOT_NUMBER') {
        [calTotalRow.prevYear, r.prevYear] = this.calUtil?.getUpdatedTotalAndRowValue(calTotalRow.prevYear, r?.prevYear,
          pyStatus === 'NEGATIVE');
      }
      const tbStatus = this.calUtil?.statusOfValue(r?.balTb);
      if (tbStatus !== 'NOT_NUMBER') {
        [calTotalRow.balTb, r.balTb] = this.calUtil?.getUpdatedTotalAndRowValue(calTotalRow.balTb, r?.balTb,
          tbStatus === 'NEGATIVE');
      }
      const uoStatus = this.calUtil?.statusOfValue(r?.underOver);
      if (uoStatus !== 'NOT_NUMBER') {
        [calTotalRow.underOver, r.underOver] = this.calUtil?.getUpdatedTotalAndRowValue(this.calUtil?.formatNumber(calTotalRow.underOver), this.calUtil?.formatNumber(r?.underOver),
          uoStatus === 'NEGATIVE');
          this.calUtil?.getUpdatedTotalAndRowValue(calTotalRow.underOver, r?.underOver,
            uoStatus === 'NEGATIVE');
      }
    });
    const updatedDiffRow = this.calUtil?.calculateDiffBtwTotalAndGL(structuredClone(calTotalRow),
      structuredClone(balanceRow));
    const updatedScheduleRows = [...structuredClone(scheduleRows), calTotalRow, balanceRow, updatedDiffRow];
    this.amortizationSchedule.set(structuredClone(updatedScheduleRows));
    this.updateAdjValues();
    this.cdr.detectChanges();
    this.submitAmortizationSchedule();
  }

  updateAdjValues() {
    const adjRows = structuredClone(this.adjustmentEntries());
    const depriciation: ScheduleAdjustmentModel = adjRows?.find((r: ScheduleAdjustmentModel) => r?.rowType === 'DEPRECIATION');
    const amortization = adjRows?.filter((r: ScheduleAdjustmentModel) => r?.rowType === 'AMORTIZATION');
    const total: ScheduleAdjustmentModel = adjRows?.find((r: ScheduleAdjustmentModel) => r?.rowType === 'TOTAL');
    const totalRow = this.amortizationSchedule()?.find(r => r?.rowType === 'Total');
    const tbRows = this.amortizationSchedule()?.filter(r => r?.rowType === 'TB');

    const uo = this.aiUtil?.formatToNumber(typeof totalRow?.underOver === 'string' ? totalRow?.underOver : totalRow?.underOver?.toString());
    if (uo > 0) {
      depriciation.debit = typeof totalRow?.underOver === 'string' ? totalRow?.underOver : totalRow?.underOver?.toString();
      total.debit = depriciation.debit;
      total.credit = depriciation.debit;
      amortization?.forEach(a => {
        const tbRow = tbRows?.find(t => t?.amortTbId === a?.trialBalId);
        if (tbRow?.trialBalId) {
          a.credit = typeof tbRow?.underOver === 'string' ? tbRow?.underOver : tbRow?.underOver?.toString();
          a.debit = '0.00';
        }
      });
    } else {
      depriciation.credit = typeof totalRow?.underOver === 'string' ? totalRow?.underOver : totalRow?.underOver?.toString();
      total.credit = depriciation.credit;
      total.debit = depriciation.credit;
      amortization?.forEach(a => {
        const tbRow = tbRows?.find(t => t?.amortTbId === a?.trialBalId);
        if (tbRow?.trialBalId) {
          a.debit = typeof tbRow?.underOver === 'string' ? tbRow?.underOver : tbRow?.underOver?.toString();
          a.credit = '0.00';
        }
      });
    }
    const updatedEntries = [depriciation, ...amortization, total];
    this.adjustmentEntries.set(structuredClone(updatedEntries));

  }

  public updateVr(event, type: 'PERCENT' | 'VALUE') {
    const val = event?.target as HTMLInputElement;
    const value = val?.value ?? '';
    const transformedValue = this.aiUtil?.formatAndTransformValue(value);
    if (type === 'PERCENT') {
      this.vrPercent.setValue(transformedValue);
    } else if (type === 'VALUE') {
      this.vrValue.setValue(transformedValue);
    }
    this.checkVrStatus();
  }

  private checkVrStatus() {
    if (this.inputType() === 'VARIANCE_ANALYSIS_INPUTS') {
      const hasValidInput = this.aiUtil?.hasVrInputsValid(this.vrPercent.getRawValue(), this.vrValue.getRawValue());
      this.hasValidVr.set(hasValidInput);
    }
  }

  public submitVr() {
    this.updateSelection.emit({
      inputType: this.inputType(),
      matPercent: this.calUtil?.parseAndReturnAbs(this.vrPercent.getRawValue()),
      matVal: this.calUtil?.parseAndReturnAbs(this.vrValue.getRawValue())
    });
  }

  public onFileSelected(event: any, type: 'DND' | 'MANUAL', hasFileLimit: boolean = true) {
    let files: any[] = type === 'DND' ? event : event?.target?.files;
    let selectedFiles = [];
    if (files && files?.length > 0) {
      selectedFiles = Array.from(files);
    }
    let totalFileSizeMB = 0;
    selectedFiles?.forEach((f: File) => {
      totalFileSizeMB += f.size / (1024 * 1024);
    });
    if ((hasFileLimit && (totalFileSizeMB > 25 || selectedFiles?.length > 15)) || (!hasFileLimit && selectedFiles?.length > 1)) {
      this.hasLimitExceeded.set(true);
    } else {
      this.hasLimitExceeded.set(false);
    }
    this.fileUploader.nativeElement.value = '';
    this.selectedFile.set(structuredClone(selectedFiles));
  }

  public removeFile(i: number) {
    this.selectedFile.update((f) => {
      return f?.filter((file, index) => index !== i);
    });
    if (this.hasLimitExceeded()) {
      const files = structuredClone(this.selectedFile());
      let totalFileSizeMB = 0;
      files?.forEach((f: File) => {
        totalFileSizeMB += f.size / (1024 * 1024);
      });
      if ((totalFileSizeMB > 25 || files?.length > 15)) {
        this.hasLimitExceeded.set(true);
      } else {
        this.hasLimitExceeded.set(false);
      }
    }
  }

  public submitFiles(submitType: 'PROCEED' | 'SUBMIT' | 'UPLOAD') {
    this.hasConfirmation.set(submitType === 'PROCEED');
    if (submitType === 'UPLOAD') {

    }
    this.updateSelection.emit({
      inputType: this.inputType(),
      selectedFiles: this.selectedFile(),
      hasConfirmation: this.hasConfirmation(),
      attachedFiles: this.aiUtil?.convertFilesToSaveInfo(structuredClone(this.selectedFile())),
      message: this.messages()
    });
  }

  private setUpGL(info: Partial<LucaInputsModel>) {
    if (info?.glSelection?.isDisabled) {
      this.selectedYear.set(info?.glSelection?.year);
      this.glData.set(info?.glSelection?.originalGl);
      this.selectedGroups = info?.glSelection?.groups;
      this.selectedSubGroups = info?.glSelection?.subGroups;
      this.selectedLs = info?.glSelection?.leadSheets;
      this.selectedAccounts = info?.glSelection?.accounts;
      this.selectedTbRows.set(info?.glSelection?.selectedRows);
    } else {
      this.glData.set(info?.glSelection?.originalGl);
      this.selectedYear.set(info?.glSelection?.year);
      this.disableSubmit.set(true);
      if (info?.glSelection?.year) {
        this.selectedGroups = info?.glSelection?.groups;
        this.selectedSubGroups = info?.glSelection?.subGroups;
        this.selectedLs = info?.glSelection?.leadSheets;
        this.selectedAccounts = info?.glSelection?.accounts;
        this.selectedTbRows.set(info?.glSelection?.selectedRows);
        this.changeYear(this.glData()?.find(g => g?.year === info?.glSelection?.year), false);
      } else {
        this.changeYear(this.glData()[0]);
      }
    }
  }

  public updateAmountInDetail(event: any, type: 'ADD' | 'DIS', detailIndex: number, index: number) {
    const val = event?.target as HTMLInputElement;
    const value = val?.value ?? '';
    const transformedValue = this.calUtil?.transformValue(value);
    const rows = JSON.parse(JSON.stringify(this.amortizationSchedule()));
    const currentRow: AmortizationScheduleModel = rows[index];
    const detail: AmorCalculationModel = currentRow?.calculations?.details[detailIndex];
    detail.amount = transformedValue;
    currentRow.calculations.details[detailIndex] = detail;
    detail.amortization = this.calUtil.calculateAmortizationForGL({...detail}, currentRow?.calculations?.method);
    const totalAmortization = this.calUtil.calculateAmortization(JSON.parse(JSON.stringify(currentRow?.calculations?.details)));
    currentRow?.calculations?.details?.forEach(r => {
      if (r?.rowType === 'TOTAL') {
        r.amortization = totalAmortization;
      }
    });
    currentRow.amortization = totalAmortization;
    this.updateScheduleValues(JSON.parse(JSON.stringify(rows)));
  }

  public closePanel(row: AmortizationScheduleModel) {
    if (!row?.isExpanded) {
      return;
    }
    this.amortizationSchedule.update(rows => {
      rows.forEach(r => {
        if (r?.trialBalId === row?.trialBalId) {
          r.isExpanded = false;
        }
      });
      return JSON.parse(JSON.stringify(rows));
    })
  }

  public openPanel(row: AmortizationScheduleModel) {
    if (row?.isExpanded) {
      return;
    }
    this.amortizationSchedule.update(rows => {
      rows.forEach(r => {
        if (r?.trialBalId === row?.trialBalId) {
          r.isExpanded = true;
        }
      });
      return JSON.parse(JSON.stringify(rows));
    })
  }


  public submitLAS() {
    this.updateSelection.emit({
      inputType: this.inputType(),
      hasMissingInputs: true,
      message: `Sure! Please upload the required documents, and we\'ll get started on generating your Loan Amortization Schedule.`,
      loanInfo: {
        'principalAmount': this.calUtil?.parseAndReturnAbs(this.principalCtrl.getRawValue()),
        'loanStartDate': this.loanStartDate(),
        'interestRate': this.calUtil?.parseAndReturnAbs(this.interestCtrl.getRawValue()),
        'frequency': this.frequencyCtrl.getRawValue(),
        'loanTenure': this.calUtil?.parseAndReturnAbs(this.loanTenure.getRawValue())
      }
    });
  }

  public transformLAS(event, allowMinus: boolean = false) {
    const val = event?.target as HTMLInputElement;
    const key = event?.key;
    const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', 'Backspace', 'Tab', 'Delete', 'ArrowLeft', 'ArrowRight'];
    if (!allowedKeys?.includes(key) || (allowMinus && key === '-')) {
      event?.preventDefault();
    }
    if (key === '.' && val?.value?.includes('.')) {
      event?.preventDefault();
    }
    if (key === '-' && val?.selectionStart !== 0) {
      event.preventDefault();
    }
  }

  public transformLASNumber(event) {
    const key = event?.key;
    const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'Backspace', 'Tab', 'Delete', 'ArrowLeft', 'ArrowRight'];
    if (!allowedKeys?.includes(key)) {
      event?.preventDefault();
    }
  }

  public updateLAS(event: any, type: 'FREQUENCY' | 'START_DATE' | 'PAYMENTS' | 'PRINCIPAL' | 'INTEREST') {
    const val = event?.target as HTMLInputElement;
    const value = val?.value ?? '';
    const transformedValue = this.aiUtil?.formatAndTransformValue(value);
    if (type === 'PRINCIPAL') {
      this.principalCtrl.setValue(transformedValue);
    }
    if (type === 'INTEREST') {
      this.interestCtrl.setValue(transformedValue);
    }
  }

  public dateChanged(date: any) {
    const transformedDate = this.datePipe.transform(date, 'MM/dd/yyyy');
    this.loanStartDate.set(transformedDate);
  }

  convertToIsoUtc(dateStr: string): string {
    if(!dateStr) return null;
    const [month, day, year] = dateStr.split('/');
    const date = new Date(Date.UTC(+year, +month - 1, +day));
    return date.toISOString(); // e.g., 2025-04-15T00:00:00.000Z
  }

  public onTabChange(tabIndex: number,methodName: string) {
    const methodId = this.aiUtil.getMethodInfo(methodName);
    tabIndex === 1 && this.showMethodFormulae.set(this.capitalFormulae[this.formulaKeys[methodId - 1]])
  }

  public submitRecon() {
    this.updateSelection.emit({
      inputType: this.inputType(),
      selectedFiles: this.selectedFile(),
      hasConfirmation: this.hasConfirmation(),
      attachedFiles: this.aiUtil?.convertFilesToSaveInfo(structuredClone(this.selectedFile())),
      message: this.messages(),
      reconInfo: {
        selectedTBAccountUUID: this.tbAccCtrl.getRawValue() ?? '',
        hasPlaid: this.plaidInstitutions()?.length > 0,
        selectedPlaidAccount: this.plaidAccCtrl.getRawValue() ?? 0,
        tbAccounts: this.tbAccounts(),
        plaidInstitutions: this.plaidInstitutions()
      }
    });
  }

}
